import { CurrentPrices, IFlowDefiningAttributes } from '@/src/utils/types';
import { LensPackage } from '@/utils/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type WorkflowState = {
  backofficeConfig: any;
  steps?: any[];
  defaultComponents: any;
  currentStep: any;
  selectedPrescriptionMode: boolean;
  currentStepPackages: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  };
  currentStepFilteredPackages: LensPackage[];
  currentStepParams: { [key: string]: string };
  currentStepProceedFn: null | (() => void);
  currentStepPrices: CurrentPrices;
  loadPreselect: boolean;
  isEditMode: boolean;
  reviewObjectForCart: any;
  flowDefiningAttributes: IFlowDefiningAttributes | null;
  isLensAdvisorOpened: boolean;
  designTypeSelected?: string;
  selectedWarranty?: any;
};

const initialState: WorkflowState = {
  backofficeConfig: null,
  steps: null,
  defaultComponents: null,
  currentStep: {},
  selectedPrescriptionMode: false,
  currentStepPackages: null,
  currentStepFilteredPackages: null,
  currentStepParams: {},
  currentStepProceedFn: null,
  currentStepPrices: {
    frame: { price: 0, discount: 0, insPrice: 0 },
    lens: { price: 0, discount: 0, insPrice: 0 },
    total: { price: 0, discount: 0, insPrice: 0 },
  },
  loadPreselect: null,
  isEditMode: false,
  reviewObjectForCart: null,
  flowDefiningAttributes: null,
  isLensAdvisorOpened: false,
  designTypeSelected: undefined,
  selectedWarranty: null,
};

const workflowSlice = createSlice({
  name: 'workflow',
  initialState: initialState,
  reducers: {
    setBackofficeConfig: (state, { payload }: PayloadAction<any>) => {
      state.backofficeConfig = payload;

      // store steps
      state.steps = payload.stepSection.steps.map((step, i) => ({
        ...step,
        definingAttributes: { ...step.definingAttributes, order: i + 1 },
      }));

      // set first step
      state.currentStep = state.steps.find(
        (step) => step?.definingAttributes?.order == 1
      );

      // store all default components
      let componentsObject = {};
      payload.defaultComponents.forEach((comp) => {
        componentsObject[comp.id] = { ...comp };
      });
      state.defaultComponents = componentsObject;

      // store flow defining attributes
      state.flowDefiningAttributes = payload.definingAttributes;

      // update brain with flow attributes
      window.rxcBrain.flowDefiningAttributes = payload.definingAttributes;

      // set stepChain in Workflow class of the Brain as an array of strings ["step1", "step2", "step3"]
      // mapped using the analytics id in order to have a unique identifier since the step title is dynamic (see getStepSelections() and getStepKeyFromAnalyticsId() functions)
      window.rxcBrain.setStepChain(
        payload.stepSection.steps.map(({ definingAttributes }) => ({
          key: definingAttributes.step_id,
          title: definingAttributes.title,
          analytics_ID: definingAttributes.analytics_ID,
          analytics_section: definingAttributes.analytics_section,
        }))
      );
    },
    setCurrentStep: (state, action: PayloadAction<any[] | any>) => {
      const currentProgressive = action.payload?.progressive;
      if (!currentProgressive) return;
      state.currentStep = state.steps.find(
        (step) => step?.definingAttributes?.order == currentProgressive
      );

      // if the current step is last step add isLastStep property
      if (state.currentStep.definingAttributes.order == state.steps.length) {
        state.currentStep.isLastStep = true;
      }

      // set current step packages
      state.currentStepPackages = window.rxcBrain.allCurrentPackages;

      // set current step total prices
      state.currentStepPrices = window.rxcBrain.currentPrices;

      // reset currentStepProceedFn
      state.currentStepProceedFn = null;

      // set grayout mode
      window.rxcBrain.enableGrayout =
        state.currentStep.definingAttributes.hasGreyout;

      // set checkPrescriptionIssueDate for manual input date
      if (state.currentStep?.definingAttributes?.checkPrescriptionIssueDate)
        window.rxcBrain.checkPrescriptionIssueDate =
          state.currentStep.definingAttributes.checkPrescriptionIssueDate;

      // set hide your selection button
      if (state.currentStep?.definingAttributes?.hideYourSelection)
        window.rxcBrain.hideYourSelection =
          state.currentStep.definingAttributes.hideYourSelection;
    },
    setSelectedPrescriptionMode: (state, { payload }: PayloadAction<any>) => {
      state.selectedPrescriptionMode = payload;
    },
    setCurrentStepParams: (state, { payload }: PayloadAction<any>) => {
      state.currentStepParams = { ...state.currentStepParams, ...payload };
      if (!payload.polar) {
        delete state.currentStepParams.polar;
      }
    },
    setCurrentStepProceedFn: (state, { payload }: PayloadAction<any>) => {
      //function to be called on confirm button (RXC_FOOTER)
      state.currentStepProceedFn = payload;
    },
    setCurrentStepPrices: (state) => {
      state.currentStepPrices = window.rxcBrain.currentPrices;
    },
    enablePreselect: (state, { payload }: PayloadAction<boolean>) => {
      state.loadPreselect = payload;
    },
    setIsEditMode: (state, { payload }: PayloadAction<any>) => {
      state.isEditMode = payload;
    },
    setReviewObjectForCart: (state, { payload }: PayloadAction<any>) => {
      state.reviewObjectForCart = payload;
    },
    setFlowDefiningAttributes: (state, { payload }: PayloadAction<any>) => {
      state.flowDefiningAttributes = payload;
    },
    setIsLensAdvisorOpened: (state, { payload }: PayloadAction<any>) => {
      state.isLensAdvisorOpened = payload;
    },
    setDesignTypeSelected: (state, { payload }: PayloadAction<any>) => {
      state.designTypeSelected = payload;
    },
    setSelectedWarranty: (state, { payload }: PayloadAction<any>) => {
      state.selectedWarranty = payload;
    },
    // Reset workflow state to initial state
    reset: (state, { payload }: PayloadAction<string>) => {
      Object.keys(initialState).forEach((key) => {
        state[key] = initialState[key];
      });
    },
  },
});

export const workflowReducer = workflowSlice.reducer;
export const workflowActions = workflowSlice.actions;
